import { Injectable, inject } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { take } from 'rxjs/operators';

import { AppState } from '../app.state';
import { ProductV2 } from '../../models/product-v2.model';
import { selectLoginData } from '../user/user.selectors';

import { AvailableOffersState } from './available-offers.state';
import {
  selectAvailableOffersState,
  selectMsisdnsByVariation,
  selectAvailableOffersLoading,
  selectAvailableOffersError,
  selectMsisdnsByVariationUuid,
  selectHasAvailableMsisdns,
} from './available-offers.selectors';
import * as AvailableOffersActions from './available-offers.actions';

@Injectable({
  providedIn: 'root'
})
export class AvailableOffersFacade {
  private readonly store = inject(Store<AppState>);

  // Selectors
  getAvailableOffersState(): Observable<AvailableOffersState> {
    return this.store.select(selectAvailableOffersState);
  }

  getMsisdnsByVariation(): Observable<Record<string, string[]>> {
    return this.store.select(selectMsisdnsByVariation);
  }

  getMsisdnsByVariationUuid(variationUuid: string): Observable<string[]> {
    return this.store.select(selectMsisdnsByVariationUuid(variationUuid));
  }

  isLoading(): Observable<boolean> {
    return this.store.select(selectAvailableOffersLoading);
  }

  getError(): Observable<string | null> {
    return this.store.select(selectAvailableOffersError);
  }

  hasAvailableMsisdns(): Observable<boolean> {
    return this.store.select(selectHasAvailableMsisdns);
  }

  // Actions
  loadAvailableOffers(cnpj: string): void {
    this.store.dispatch(AvailableOffersActions.loadAvailableOffers({ cnpj }));
  }

  clearAvailableOffers(): void {
    this.store.dispatch(AvailableOffersActions.clearAvailableOffers());
  }

  mapOffersToVariations(offerToVariationMap: Record<string, string[]>): void {
    this.store.dispatch(AvailableOffersActions.mapOffersToVariations({ offerToVariationMap }));
  }

  /**
   * Mapeia offers para variations de um produto específico
   * Deve ser chamado quando um produto for carregado (getProductByUuid)
   * Só funciona se o usuário fez mobile login (verifica se tem resources)
   */
  mapProductVariationsToOffers(product: ProductV2): void {
    this.store.select(selectLoginData).pipe(take(1)).subscribe(loginData => {
      const isMobileLogin = !!(loginData?.resources && loginData.resources.length > 0);
      
      if (!isMobileLogin) {
        return;
      }

      const offerToVariationMap: Record<string, string[]> = {};

      product.variations?.forEach(variation => {
        // Busca o customField com label "cpcOfferId"
        const offerUuidField = variation.customFields?.find(
          field => field.label === 'cpcOfferId'
        );

        if (offerUuidField?.value) {
          const cpcOfferId = offerUuidField.value;
          if (!offerToVariationMap[cpcOfferId]) {
            offerToVariationMap[cpcOfferId] = [];
          }
          offerToVariationMap[cpcOfferId].push(variation.uuid);
        }
      });

      if (Object.keys(offerToVariationMap).length > 0) {
        this.mapOffersToVariations(offerToVariationMap);
      }
    });
  }

  /**
   * Mapeia offers para variations de múltiplos produtos
   * Deve ser chamado quando carregamos produtos por categoria (getProductsByCategory)
   * Só funciona se o usuário fez mobile login (verifica se tem resources)
   */
  mapProductsVariationsToOffers(products: ProductV2[]): void {
    this.store.select(selectLoginData).pipe(take(1)).subscribe(loginData => {
      const isMobileLogin = !!(loginData?.resources && loginData.resources.length > 0);
      
      if (!isMobileLogin) {
        return;
      }

      const offerToVariationMap: Record<string, string[]> = {};

      products.forEach(product => {
        product.variations?.forEach(variation => {
          // Busca o customField com label "cpcOfferId"
          const offerUuidField = variation.customFields?.find(
            field => field.label === 'cpcOfferId'
          );

          if (offerUuidField?.value) {
            const cpcOfferId = offerUuidField.value;
            if (!offerToVariationMap[cpcOfferId]) {
              offerToVariationMap[cpcOfferId] = [];
            }
            offerToVariationMap[cpcOfferId].push(variation.uuid);
          }
        });
      });

      if (Object.keys(offerToVariationMap).length > 0) {
        this.mapOffersToVariations(offerToVariationMap);
      }
    });
  }
}

